/**
 * Tanjong Life · "commission a Retention Sentinel" — and the honest answer, which is that it is
 * already running.
 *
 * THIS BEAT EXISTS BECAUSE THE ALTERNATIVE WRITES A WRONG RECORD. The surface router reads a
 * making verb plus the word `agent` and hands the sentence to the generic agent builder,
 * which consults nothing scripted, invents a name and persists it into this org. Four of the
 * six autonomous agents here were commissioned before the demo starts, so the phrasing that
 * commissions the other two has to land somewhere truthful rather than somewhere plausible.
 *
 * It creates nothing, and that is deliberate rather than a shortcut. Pointing the commission
 * conversation at a seeded pk would REPLACE the agent row and its whole detail bundle —
 * `demo-born.ts` upserts by pk — and with no boot asset the page would never play its stream
 * again. A fully authored agent would be overwritten to narrate a birth its own v1 release
 * row already dates to 24 August, with a named proposer and approver on it.
 *
 * So it says so, names what it watches and the one thing it refuses to claim, and opens it.
 */
import type { Scenario } from '../director.js';
import { MAKE_RETENTION_SENTINEL } from './tanjong-make-intents.js';

export const tanjongStandingRetention: Scenario = {
  id: 'tanjong-standing-retention',
  orgId: 'tanjong-life',
  /*
   * NO KEYWORDS. `settlement assurance sentinel` as a keyword stole "Build a process for
   * claims payment authorisation with the Settlement Assurance Sentinel driving it" from the
   * build it plainly asks for — these scenarios register before the builds, so a keyword here
   * wins any sentence that merely NAMES the agent. Meaning is the only safe route in.
   */
  keywords: [],
  intents: [MAKE_RETENTION_SENTINEL],
  surface: 'chat',
  steps: [
    {
      do: 'say',
      text: 'The Retention Sentinel already exists — it was commissioned on 24 August by Stefan Oyelaran, approved by Farah Idris, and it has been running ever since. I am not going to stand a second one up beside it: two agents watching the same queue is the thing this estate is built to prevent, and the second one would have no way of knowing what the first had already opened.',
      sources: [
        'Retention Sentinel · v1 live since 24 August',
        'Proposed by Stefan Oyelaran · approved by Farah Idris',
      ],
      delay: 900,
    },
    {
      do: 'insight',
      title: 'What it already watches',
      body: 'Workflow 03 · policy lapse prevention ends when a person has what they need. This agent watches what happens to an at-risk flag after the ranking is published — the ageing, the delivery, the suppression band’s own error rate. It runs 4 probes, 1 held, opens a case at a named person with the evidence attached, and holds all five of its authorities at zero of zero with Halt as the action on breach — it reports, and it decides nothing.',
      sources: ['Process tl-pr13', 'Authorities · 5, every one 0 of 0 · Halt on breach'],
      delay: 1300,
    },
    {
      do: 'say',
      text: 'And the thing worth hearing before you take my word for any of it: Whether a save was a save, at 34 out of 100. Without a cohort held back from contact, a household that stayed and a household that stayed because of us are the same row, and it will not publish the second as if it were the first. Its overall evaluation is 80.',
      delay: 1300,
    },
    {
      do: 'link',
      label: 'Open Retention Sentinel',
      href: '/agents/retention-sentinel',
      delay: 600,
    },
  ],
};
